import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

/**
 * Button component for consistent button styling across the app
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  disabled = false,
  type = 'button',
  onClick,
  className = '',
  as: Component = 'button',
  ...rest
}) => {
  // Base styles that apply to all buttons
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-colors duration-200 rounded-md focus:outline-none';
  
  // Variant-specific styles
  const variantStyles = {
    primary: 'bg-deep-red hover:bg-bright-red text-white shadow-sm',
    secondary: 'border border-deep-red text-deep-red hover:text-bright-red hover:border-bright-red bg-white hover:bg-light-grey',
    link: 'text-deep-red hover:text-bright-red underline bg-transparent',
    outline: 'border border-medium-grey/30 bg-white hover:border-deep-red/50 text-dark-grey',
    dark: 'bg-dark-grey hover:bg-medium-grey text-white shadow-sm',
  };
  
  // Size-specific styles
  const sizeStyles = {
    xs: 'text-xs px-2 py-1 space-x-1',
    sm: 'text-xs px-3 py-1.5 space-x-1',
    md: 'text-sm px-4 py-2 space-x-2',
    lg: 'text-base px-5 py-2.5 space-x-2'
  };
  
  // Width styles
  const widthStyles = fullWidth ? 'w-full' : '';
  
  // Disabled styles
  const disabledStyles = disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer';
  
  // Combine all styles
  const buttonClasses = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${disabledStyles} ${className}`;
  
  // Handle icon rendering
  const renderIcon = () => {
    if (!icon) return null;
    return <FontAwesomeIcon icon={icon} className={iconPosition === 'right' ? 'ml-2' : 'mr-2'} />;
  };
  
  // Only include the type prop if we're rendering an actual button
  const buttonProps = Component === 'button' ? { type } : {};
  
  return (
    <Component
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...buttonProps}
      {...rest}
    >
      {iconPosition === 'left' && renderIcon()}
      {children}
      {iconPosition === 'right' && renderIcon()}
    </Component>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'link', 'outline', 'dark']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  icon: PropTypes.object, // FontAwesome icon
  iconPosition: PropTypes.oneOf(['left', 'right']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  onClick: PropTypes.func,
  className: PropTypes.string,
  as: PropTypes.elementType, // To render as different element types (like Link)
};

export default Button; 